// Instagram（ig-harness のフォロワー）を検索／最近DMした人から選ぶ部品。
// クリエイター詳細の「Instagram連携」と、クリエイター追加の「Instagramから選ぶ」で共用（旧SPA app.js の IG 検索移植）。
import { useState } from "react";
import { fetchGfIgRecent, searchGfIg } from "../../../api/giftingClient";
import { fmtDateTime } from "../../../lib/giftingRules";
import type { GfIgFriend, Loadable } from "../../../types/gifting";
import { errMsg } from "../gfUtil";
import { GfButton } from "./GfUi";

export function IgPicker({
  pickLabel,
  onPick,
  disabled,
  /** 既にクリエイターに紐付け済みの ig_follower_id（「登録済み」表示用。選ぶと既存クリエイターが使われる） */
  linkedIds,
}: {
  pickLabel: string;
  onPick: (f: GfIgFriend) => void;
  disabled?: boolean;
  linkedIds?: { has(id: number): boolean };
}) {
  const [q, setQ] = useState("");
  const [res, setRes] = useState<Loadable<GfIgFriend[]> | null>(null);
  const [label, setLabel] = useState("");

  const load = async (kind: "search" | "recent") => {
    const needle = q.trim().replace(/^@/, "");
    if (kind === "search" && !needle) return setRes({ status: "error", message: "名前かユーザー名を入力してください" });
    setRes({ status: "loading" });
    setLabel(kind === "search" ? `「${needle}」の検索結果` : "最近DMした人");
    try {
      setRes({ status: "ok", data: kind === "search" ? await searchGfIg(needle) : await fetchGfIgRecent() });
    } catch (e) {
      setRes({ status: "error", message: errMsg(e) });
    }
  };

  return (
    <div className="gf-ig-picker">
      <div className="gf-inline-row">
        <input
          className="gf-input"
          aria-label="Instagramの名前またはユーザー名"
          placeholder="名前 または ユーザー名"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              void load("search");
            }
          }}
        />
        <GfButton size="sm" disabled={res?.status === "loading"} onClick={() => void load("search")}>
          検索
        </GfButton>
        <GfButton size="sm" disabled={res?.status === "loading"} onClick={() => void load("recent")}>
          最近DMした人
        </GfButton>
      </div>
      {res?.status === "loading" && <div className="gf-note">読み込み中…</div>}
      {res?.status === "error" && <div className="gf-banner is-error">{res.message}</div>}
      {res?.status === "ok" && (
        <div className="gf-ig-results" aria-label={label}>
          <div className="gf-note">{label}（{res.data.length}件）</div>
          {res.data.length === 0 && <div className="gf-note">見つかりませんでした</div>}
          {res.data.map((f) => {
            const linked = linkedIds?.has(f.id) ?? false;
            return (
              <div className="gf-ig-row" key={f.id}>
                <span className="gf-ig-row-main">
                  <span className="gf-ig-user">
                    @{f.username || "—"}
                    {linked && <span className="gf-ig-linked">登録済み</span>}
                  </span>
                  <span className="gf-ig-sub">
                    {[f.display_name, f.last_message_at ? `最終DM ${fmtDateTime(f.last_message_at)}` : null].filter(Boolean).join("・")}
                  </span>
                </span>
                <GfButton size="sm" variant="primary" disabled={disabled} onClick={() => onPick(f)}>
                  {pickLabel}
                </GfButton>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
